import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import AdvancedReportOptions from '@/common/models/reports/AdvancedReportOptions';
import { DASH, toServerDateTime } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

interface IAdvancedReportExecutionAPI {
  report_execution_id: number;
  customreportexecution_id: number;
  execution_started_time_utc: string;
  execution_finished_time_utc: string;
  organization_id: number;
  report_file_url: string;
  status_code: string;
  status_message: string;
  progress: number;
  type: string;
}

export default class AdvancedReportExecution extends Validatable {
  advancedReportExecutionId: number;
  executionStartedTimeUtc: DateTime;
  executionFinishedTimeUtc: DateTime;
  organizationId: number;
  parameters: string;
  reportFileUrl: string;
  statusCode: string;
  statusMessage: string;
  progress: number;
  type: string;

  constructor(
    id = -1,
    advancedReportExecutionId = null,
    executionStartedTimeUtc = null,
    executionFinishedTimeUtc = null,
    organizationId = null,
    parameters = null,
    reportFileUrl = null,
    statusCode = null,
    statusMessage = null,
    progress = null,
    type = null,
  ) {
    super();
    this.id = id;
    this.advancedReportExecutionId = advancedReportExecutionId;
    this.executionStartedTimeUtc = executionStartedTimeUtc;
    this.executionFinishedTimeUtc = executionFinishedTimeUtc;
    this.organizationId = organizationId;
    this.parameters = parameters;
    this.reportFileUrl = reportFileUrl;
    this.statusCode = statusCode;
    this.statusMessage = statusMessage;
    this.progress = progress;
    this.type = type;
  }

  get status() {
    switch (this.statusCode) {
      case 'ERROR':
        return capitalize(i18n.global.t('names.error', { n: 1 }));
      case 'DONE':
        return capitalize(i18n.global.t('states.done'));
      case 'QUEUED':
        return capitalize(i18n.global.t('states.queued'));
      case 'RUNNING':
        return capitalize(i18n.global.t('states.running'));
      default:
        return capitalize(i18n.global.t('states.unknown'));
    }
  }

  get isRunning() {
    return this.statusCode !== 'DONE' && this.statusCode !== 'ERROR';
  }

  get isSuccess() {
    return this.statusCode === 'DONE';
  }

  get isError() {
    return this.statusCode === 'ERROR';
  }

  get startedOnFormatted() {
    if (this.executionStartedTimeUtc !== null) {
      return this.executionStartedTimeUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  static getTypes() {
    return [
      {
        id: 'capped_impressions',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.cappedImpressions'),
      },
      {
        id: 'contending_lineitems',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.contendingLineitems'),
      },
      {
        id: 'nautilus',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.nautilus'),
      },
      {
        id: 'allvision_billing',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.allVision'),
      },
      {
        id: 'fill_rate',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.fillRate'),
      },
      {
        id: 'active_agent_report',
        name: AdvancedReportOptions.getLocalizedOptionName('reportType.activeAgentReport'),
      },
    ];
  }

  get formattedType() {
    const type = AdvancedReportExecution.getTypes().find((x) => x.id === this.type);
    if (type == null) return capitalize(i18n.global.t('names.unknown', { n: 1 }));
    return type.name;
  }

  get finishedOnFormatted() {
    if (this.executionFinishedTimeUtc !== null) {
      return this.executionFinishedTimeUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  get durationInSeconds() {
    if (this.executionStartedTimeUtc && this.executionFinishedTimeUtc) {
      let { seconds } = this.executionFinishedTimeUtc.diff(this.executionStartedTimeUtc, 'seconds').toObject();
      if (seconds === 0) {
        seconds = 1;
      }
      return seconds;
    }
    return null;
  }

  get durationInSecondsFormatted() {
    if (this.durationInSeconds !== null) {
      return `${this.durationInSeconds} ${i18n.global.t('names.second', Math.floor(this.durationInSeconds))}`;
    }
    return DASH;
  }

  get progressFormatted() {
    return formatNumber(this.progress, '0%');
  }

  static fromApi(apiObject) {
    let statusMessage = '';
    try {
      statusMessage = apiObject.status_message !== null ? JSON.parse(apiObject.status_message) : null;
    } catch (e) {
      statusMessage = apiObject.status_message;
    }
    return new AdvancedReportExecution(
      apiObject.customreportexecution_id,
      apiObject.customreportexecution_id,
      apiObject.execution_started_time_utc !== null && apiObject.execution_started_time_utc !== undefined
        ? DateTime.fromISO(apiObject.execution_started_time_utc, { zone: 'utc' })
        : null,
      apiObject.execution_finished_time_utc !== null && apiObject.execution_finished_time_utc !== undefined
        ? DateTime.fromISO(apiObject.execution_finished_time_utc, { zone: 'utc' })
        : null,
      apiObject.organization_id,
      apiObject.parameters,
      apiObject.report_file_url,
      apiObject.status_code,
      statusMessage,
      apiObject.progress,
      apiObject.type,
    );
  }

  static fromObject(obj) {
    return super.fromObject(obj, new AdvancedReportExecution());
  }

  toApi(): IAdvancedReportExecutionAPI {
    return {
      report_execution_id: this.id >= 0 ? this.id : undefined,
      customreportexecution_id: this.advancedReportExecutionId,
      execution_started_time_utc: toServerDateTime(this.executionStartedTimeUtc),
      execution_finished_time_utc: toServerDateTime(this.executionFinishedTimeUtc),
      organization_id: this.organizationId,
      report_file_url: this.reportFileUrl,
      status_code: this.statusCode,
      status_message: this.statusMessage,
      progress: this.progress,
      type: this.type,
    };
  }
}
